import React from "react";
import { Link } from "react-router-dom";

/**
 * Global compact footer — visible on landing, auth, and application shell.
 *
 * Renders the canonical legal links (Terms · Privacy · Security · Contact)
 * plus the © 2026 ForgedOps LLC line. Designed to sit at the bottom of any
 * full-height layout without breaking the existing visual hierarchy.
 *
 * Variants:
 *   - `default` — used on auth & legal/contact pages. Full border + padding.
 *   - `compact` — used inside the authenticated app Shell footer slot.
 *                  Slimmer, single-row layout, no top whitespace.
 */
export default function LegalFooter({ variant = "default" }) {
  const year = new Date().getFullYear();
  const compact = variant === "compact";

  return (
    <footer
      className={`w-full border-t ${compact ? "py-3" : "py-6"}`}
      style={{
        borderColor: "var(--fop-border)",
        background: "var(--fop-bg)",
      }}
      data-testid="legal-footer"
    >
      <div className={`mx-auto px-4 sm:px-6 ${compact ? "max-w-none" : "max-w-[1280px]"} flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3`}>
        <div
          className="text-xs sm:text-[13px] font-mono uppercase tracking-[0.18em]"
          style={{ color: "var(--fop-text-3)" }}
          data-testid="legal-footer-copy"
        >
          © {year} ForgedOps LLC
        </div>
        <nav
          className="flex flex-wrap items-center gap-x-4 sm:gap-x-5 gap-y-2 text-xs sm:text-[13px]"
          aria-label="Legal links"
        >
          <LegalLink to="/legal/terms" testid="footer-link-terms">Terms of Use</LegalLink>
          <Sep />
          <LegalLink to="/legal/privacy" testid="footer-link-privacy">Privacy Policy</LegalLink>
          <Sep />
          <LegalLink to="/legal/security" testid="footer-link-security">Security</LegalLink>
          <Sep />
          <LegalLink to="/trust" testid="footer-link-trust">Trust</LegalLink>
          <Sep />
          <LegalLink to="/support" testid="footer-link-support">Support</LegalLink>
          <Sep />
          <LegalLink to="/contact" testid="footer-link-contact">Contact</LegalLink>
        </nav>
      </div>
    </footer>
  );
}

function LegalLink({ to, children, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="font-mono uppercase tracking-[0.16em] transition-colors"
      style={{ color: "var(--fop-text-3)" }}
      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--fop-text)")}
      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--fop-text-3)")}
    >
      {children}
    </Link>
  );
}

function Sep() {
  return (
    <span
      aria-hidden="true"
      className="opacity-40 select-none"
      style={{ color: "var(--fop-text-4)" }}
    >
      |
    </span>
  );
}
